import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '../store';

Vue.use(VueRouter);

// Import components
import UserLogin from '../components/auth/UserLogin.vue';
import PlaceholderView from '../views/Placeholder.vue';

const routes = [
  // Public routes
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: UserLogin,
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: () => import('../views/RegisterChooser.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/register/company',
    name: 'RegisterCompany',
    component: () => import('../views/RegisterCompany.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/setup-account',
    name: 'SetupAccountSelf',
    component: () => import('../views/SetupAccount.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/setup-account/:setupToken',
    name: 'SetupAccount',
    component: () => import('../views/SetupAccount.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password/:token',
    name: 'ResetPassword',
    component: () => import('../views/ResetPassword.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/audition-register/:auditionId',
    name: 'AuditionPublicRegistration',
    component: () => import('../views/AuditionPublicRegistration.vue'),
    meta: { requiresGuest: false } // Public route, no auth required
  },
  {
    path: '/profile/p/:slug',
    name: 'PublicProfile',
    component: () => import('../views/PublicProfile.vue'),
    meta: { requiresAuth: false, hideLayout: true }
  },
  {
    path: '/fundraise/:campaignId',
    name: 'PublicFundraisingCampaign',
    component: () => import('../views/public/FundraisingCampaign.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/fundraise/:campaignId/:participantSlug',
    name: 'PublicFundraisingParticipant',
    component: () => import('../views/public/FundraisingCampaign.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/unsubscribed',
    name: 'UnsubscribeConfirmation',
    component: () => import('../views/public/UnsubscribeConfirmation.vue')
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('../views/public/PrivacyPolicy.vue'),
    meta: { requiresAuth: false, hideLayout: true }
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('../views/public/TermsOfService.vue'),
    meta: { requiresAuth: false, hideLayout: true }
  },

  // Protected routes - Super Admin
  {
    path: '/super-admin',
    name: 'SuperAdminDashboard',
    component: () => import('../views/admin/Dashboard.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/dashboard',
    name: 'SuperAdminDashboardFull',
    component: () => import('../views/admin/Dashboard.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/users',
    name: 'SuperAdminUsers',
    component: () => import('../views/admin/UserManagement.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/users/create',
    name: 'CreateUser',
    component: () => import('../views/admin/CreateUser.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/users/:id',
    name: 'UserDetails',
    component: () => import('../views/admin/UserDetails.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/companies',
    name: 'SuperAdminCompanies',
    component: () => import('../views/admin/CompanyManagement.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/companies/create',
    name: 'CreateCompany',
    component: () => import('../views/admin/CreateCompany.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/companies/:id',
    name: 'SuperAdminCompanyDetails',
    component: () => import('../views/admin/CompanyDetails.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/auditions',
    name: 'SuperAdminAuditions',
    component: () => import('../views/admin/AuditionManagement.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/auditions/create',
    name: 'SuperAdminCreateAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/auditions/:id/edit',
    name: 'SuperAdminEditAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/reports',
    name: 'SuperAdminReports',
    component: () => import('../views/admin/Reports.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/profile',
    name: 'SuperAdminProfile',
    component: () => import('../views/admin/Profile.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/settings',
    name: 'SuperAdminSettings',
    component: () => import('../views/admin/Settings.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/test-push-notifications',
    name: 'SuperAdminTestPushNotifications',
    component: () => import('../views/admin/TestPushNotifications.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/calendar',
    name: 'SuperAdminCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/calendar/events/create',
    name: 'SuperAdminCreateEvent',
    component: () => import('../views/admin/CreateEvent.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/calendar/events/:id/edit',
    name: 'SuperAdminEditEvent',
    component: () => import('../views/admin/CreateEvent.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/calendar/conflicts/create',
    name: 'SuperAdminCreateConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/calendar/conflicts/:id/edit',
    name: 'SuperAdminEditConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/fundraising',
    name: 'SuperAdminFundraising',
    component: () => import('../views/admin/FundraisingManagement.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/fundraising/create',
    name: 'SuperAdminCreateFundraising',
    component: () => import('../views/admin/CreateFundraisingCampaign.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },
  {
    path: '/super-admin/fundraising/:id/edit',
    name: 'SuperAdminEditFundraising',
    component: () => import('../views/admin/CreateFundraisingCampaign.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true }
  },

  // Protected routes - Admin (formerly Company)
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: () => import('../views/company/Dashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboardFull',
    component: () => import('../views/company/Dashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions',
    name: 'AdminAuditions',
    component: () => import('../views/admin/AuditionManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions/:id/manage-judges',
    name: 'ManageJudges',
    component: () => import('../views/company/ManageJudges.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions/:id/manage-performers',
    name: 'ManagePerformers',
    component: () => import('../views/company/ManagePerformers.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions/create',
    name: 'CreateAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions/:id/edit',
    name: 'EditAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/auditions/:id/start-session',
    name: 'StartAuditionSession',
    component: () => import('../views/admin/StartAuditionSession.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, hideMobileBottomNav: true }
  },
  {
    path: '/admin/auditions/:id',
    name: 'AuditionDetails',
    component: () => import('../views/judge/AuditionDetails.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/judges',
    name: 'AdminJudges',
    component: () => import('../views/company/Judges.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/judges/create',
    name: 'CreateJudge',
    component: () => import('../views/admin/CreateJudge.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/performers',
    name: 'AdminPerformers',
    component: PlaceholderView,
    props: { title: 'View Performers', description: 'Browse and manage performers', icon: 'mdi-account-music' },
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('../views/admin/UserManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users/import',
    name: 'AdminUserImport',
    component: () => import('../views/admin/UserImport.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users/create',
    name: 'AdminCreateUser',
    component: () => import('../views/admin/CreateUser.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users/:id',
    name: 'AdminUserDetails',
    component: () => import('../views/admin/UserDetails.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users/:id/edit',
    name: 'AdminEditUser',
    component: () => import('../views/admin/UserDetails.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/results',
    name: 'AdminResults',
    component: () => import('../views/company/Results.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/profile',
    name: 'AdminProfile',
    component: () => import('../views/company/Profile.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: () => import('../views/company/Settings.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/calendar',
    name: 'AdminCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/calendar/events/create',
    name: 'CreateEvent',
    component: () => import('../views/admin/CreateEvent.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/calendar/events/:id/edit',
    name: 'EditEvent',
    component: () => import('../views/admin/CreateEvent.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/calendar/conflicts/create',
    name: 'CreateConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/calendar/conflicts/:id/edit',
    name: 'EditConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/meetings',
    name: 'AdminMeetings',
    component: () => import('../views/admin/Meetings.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/meetings/create',
    name: 'CreateMeeting',
    component: () => import('../views/admin/CreateMeeting.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/classes',
    name: 'AdminClasses',
    component: () => import('../views/admin/ClassManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/classes/create',
    name: 'CreateClass',
    component: () => import('../views/admin/CreateClass.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/classes/:id',
    name: 'ViewClass',
    component: () => import('../views/admin/CreateClass.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/classes/:id/edit',
    name: 'EditClass',
    component: () => import('../views/admin/CreateClass.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/attendance',
    name: 'AdminAttendance',
    component: () => import('../views/admin/Attendance.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions',
    name: 'AdminProductions',
    component: () => import('../views/admin/Productions.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/create',
    name: 'CreateProduction',
    component: () => import('../views/admin/CreateProduction.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:id',
    name: 'ProductionDetails',
    component: () => import('../views/admin/ProductionDetails.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:id/edit',
    name: 'EditProduction',
    component: () => import('../views/admin/CreateProduction.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:id/announcements',
    name: 'ProductionAnnouncements',
    component: () => import('../views/admin/ProductionAnnouncements.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:id/reminders',
    name: 'ProductionReminders',
    component: () => import('../views/admin/ProductionReminders.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:id/todos',
    name: 'ProductionTodos',
    component: () => import('../views/admin/ProductionTodos.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:productionId/rehearsals/create',
    name: 'CreateRehearsal',
    component: () => import('../views/admin/CreateRehearsal.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:productionId/program/import-script',
    name: 'ImportScript',
    component: () => import('../views/admin/ImportScript.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/productions/:productionId/rehearsals/:id/edit',
    name: 'EditRehearsal',
    component: () => import('../views/admin/CreateRehearsal.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/ticketing',
    name: 'AdminTicketing',
    component: () => import('../views/admin/Ticketing.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/permissions',
    name: 'AdminPermissions',
    component: () => import('../views/admin/Permissions.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/fundraising',
    name: 'AdminFundraisingManagement',
    component: () => import('../views/admin/FundraisingManagement.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/fundraising/create',
    name: 'AdminCreateFundraising',
    component: () => import('../views/admin/CreateFundraisingCampaign.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/fundraising/:id/edit',
    name: 'AdminEditFundraising',
    component: () => import('../views/admin/CreateFundraisingCampaign.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },

  // Protected routes - Judge (dynamic permission-driven dashboard)
  {
    path: '/judge',
    name: 'JudgeDashboard',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/dashboard',
    name: 'JudgeDashboardFull',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  // Deprecated judge My Auditions list — redirect away
  {
    path: '/judge/auditions',
    redirect: '/judge'
  },
  {
    path: '/judge/sessions',
    name: 'JudgeSessions',
    component: () => import('../views/judge/Sessions.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/profile',
    name: 'JudgeProfile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/settings',
    name: 'JudgeSettings',
    component: () => import('../views/judge/Settings.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/schedule',
    name: 'JudgeSchedule',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/scoring',
    name: 'JudgeScoring',
    component: () => import('../views/judge/Scoring.vue'),
    meta: { requiresAuth: true, requiresJudge: true, hideMobileBottomNav: true }
  },
  {
    path: '/judge/test-scoring',
    name: 'TestScoring',
    component: () => import('../views/judge/TestScoring.vue'),
    meta: { requiresAuth: true, requiresJudge: true, hideMobileBottomNav: true }
  },
  {
    path: '/judge/template-preview',
    name: 'TemplatePreview',
    component: () => import('../views/judge/TemplatePreview.vue'),
    meta: { requiresAuth: true, requiresJudge: true, hideMobileBottomNav: true }
  },
  {
    path: '/judge/auditions/:id/summary',
    name: 'AuditionSummary',
    component: () => import('../views/judge/AuditionSummary.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  {
    path: '/judge/auditions/:id/casting',
    name: 'AuditionCasting',
    component: () => import('../views/judge/AuditionCasting.vue'),
    meta: { requiresAuth: true, requiresJudge: true }
  },
  // Deprecated judge audition details — redirect to scoring (after summary/casting)
  {
    path: '/judge/auditions/:id',
    redirect: (to) => ({
      path: '/judge/scoring',
      query: { auditionId: to.params.id }
    })
  },

  // Protected routes - Instructor (dynamic permission-driven dashboard)
  {
    path: '/instructor',
    name: 'InstructorDashboard',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/dashboard',
    name: 'InstructorDashboardFull',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/classes',
    name: 'InstructorClasses',
    component: () => import('../views/instructor/Classes.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/attendance',
    name: 'InstructorAttendance',
    component: () => import('../views/admin/Attendance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/calendar',
    name: 'InstructorCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/profile',
    name: 'InstructorProfile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/settings',
    name: 'InstructorSettings',
    component: () => import('../views/instructor/Settings.vue'),
    meta: { requiresAuth: true }
  },

  // Protected routes - Production Manager (dynamic permission-driven dashboard)
  {
    path: '/production-manager',
    name: 'ProductionManagerDashboard',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/dashboard',
    name: 'ProductionManagerDashboardFull',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/registrations',
    name: 'ProductionManagerRegistrations',
    component: () => import('../views/production-manager/Registrations.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/auditions',
    name: 'ProductionManagerAuditions',
    component: () => import('../views/production-manager/Auditions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/auditions/create',
    name: 'ProductionManagerCreateAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/auditions/:id/edit',
    name: 'ProductionManagerEditAudition',
    component: () => import('../views/admin/CreateAudition.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/auditions/:id',
    name: 'ProductionManagerAuditionDetails',
    component: () => import('../views/judge/AuditionDetails.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/auditions/:id/summary',
    name: 'ProductionManagerAuditionSummary',
    component: () => import('../views/judge/AuditionSummary.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/calendar',
    name: 'ProductionManagerCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/settings',
    name: 'ProductionManagerSettings',
    component: () => import('../views/production-manager/Settings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions',
    name: 'ProductionManagerProductions',
    component: () => import('../views/admin/Productions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/create',
    name: 'ProductionManagerCreateProduction',
    component: () => import('../views/admin/CreateProduction.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:id',
    name: 'ProductionManagerProductionDetails',
    component: () => import('../views/admin/ProductionDetails.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:id/edit',
    name: 'ProductionManagerEditProduction',
    component: () => import('../views/admin/CreateProduction.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:id/announcements',
    name: 'ProductionManagerAnnouncements',
    component: () => import('../views/admin/ProductionAnnouncements.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:id/reminders',
    name: 'ProductionManagerReminders',
    component: () => import('../views/admin/ProductionReminders.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:id/todos',
    name: 'ProductionManagerTodos',
    component: () => import('../views/admin/ProductionTodos.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:productionId/rehearsals/create',
    name: 'ProductionManagerCreateRehearsal',
    component: () => import('../views/admin/CreateRehearsal.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:productionId/program/import-script',
    name: 'ProductionManagerImportScript',
    component: () => import('../views/admin/ImportScript.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/productions/:productionId/rehearsals/:id/edit',
    name: 'ProductionManagerEditRehearsal',
    component: () => import('../views/admin/CreateRehearsal.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/production-manager/audition-queue/:sessionId',
    name: 'ProductionManagerAuditionQueue',
    component: () => import('../views/production-manager/AuditionQueue.vue'),
    meta: { requiresAuth: true }
  },

  // Protected routes - Fundraising Leader
  {
    path: '/fundraising-leader',
    redirect: '/fundraising-leader/dashboard'
  },
  {
    path: '/fundraising-leader/dashboard',
    redirect: '/fundraising-leader/fundraising'
  },
  {
    path: '/fundraising-leader/fundraising',
    name: 'FundraisingLeaderCampaign',
    component: () => import('../views/fundraising-leader/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/fundraising-leader/calendar',
    name: 'FundraisingLeaderCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/fundraising-leader/profile',
    name: 'FundraisingLeaderProfile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/fundraising-leader/outreach/:campaignId/templates',
    name: 'OutreachTemplates',
    component: () => import('../views/fundraising-leader/OutreachTemplates.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/fundraising-leader/outreach/:campaignId/analytics',
    name: 'OutreachAnalytics',
    component: () => import('../views/fundraising-leader/OutreachAnalytics.vue'),
    meta: { requiresAuth: true }
  },

  // Protected routes - Performer
  {
    path: '/performer',
    name: 'PerformerDashboard',
    component: () => import('../views/performer/Dashboard.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/dashboard',
    name: 'PerformerDashboardFull',
    component: () => import('../views/performer/Dashboard.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/auditions',
    name: 'PerformerAuditions',
    component: () => import('../views/performer/Auditions.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/auditions/:id',
    name: 'PerformerAuditionDetails',
    component: () => import('../views/performer/AuditionDetails.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/applications',
    name: 'PerformerApplications',
    component: () => import('../views/performer/Applications.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/profile',
    name: 'PerformerProfile',
    component: () => import('../views/performer/Profile.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/settings',
    name: 'PerformerSettings',
    component: () => import('../views/performer/Settings.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/materials',
    name: 'PerformerMaterials',
    component: () => import('../views/performer/Materials.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/calendar',
    name: 'PerformerCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/calendar/conflicts/create',
    name: 'PerformerCreateConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/calendar/conflicts/:id/edit',
    name: 'PerformerEditConflict',
    component: () => import('../views/admin/CreateConflict.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/cast-history',
    name: 'PerformerCastHistory',
    component: () => import('../views/performer/CastHistory.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/fundraising',
    name: 'PerformerFundraising',
    component: () => import('../views/performer/Fundraising.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/productions',
    name: 'PerformerProductions',
    component: () => import('../views/performer/Productions.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/performer/productions/:id',
    name: 'PerformerProductionDetails',
    component: () => import('../views/performer/ProductionDetails.vue'),
    meta: { requiresAuth: true, requiresPerformer: true }
  },
  {
    path: '/meeting/:id',
    name: 'Meeting',
    component: () => import('../views/Meeting.vue'),
    meta: { requiresAuth: true }
  },

  // Common protected routes
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true }
  },
  
  // Dynamic routes for custom userTypes (permission-driven dashboard for any role)
  {
    path: '/:userType/dashboard',
    name: 'DynamicDashboard',
    component: () => import('../views/DynamicRoleDashboard.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/calendar',
    name: 'DynamicCalendar',
    component: () => import('../views/Calendar.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/auditions',
    name: 'DynamicAuditions',
    component: () => import('../views/company/Auditions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/productions',
    name: 'DynamicProductions',
    component: () => import('../views/admin/Productions.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/users',
    name: 'DynamicUsers',
    component: () => import('../views/admin/UserManagement.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/registrations',
    name: 'DynamicRegistrations',
    component: () => import('../views/production-manager/Registrations.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/classes',
    name: 'DynamicClasses',
    component: () => import('../views/instructor/Classes.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/attendance',
    name: 'DynamicAttendance',
    component: () => import('../views/admin/Attendance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/meetings',
    name: 'DynamicMeetings',
    component: () => import('../views/admin/Meetings.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/results',
    name: 'DynamicResults',
    component: () => import('../views/company/Results.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/chats',
    name: 'DynamicChats',
    component: () => import('../views/Chat.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/:userType/profile',
    name: 'DynamicProfile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/help',
    name: 'Help',
    component: () => import('../views/Help.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('../views/NotificationCenter.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/Profile.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: PlaceholderView,
    props: { title: 'Settings', description: 'Manage your account settings', icon: 'mdi-cog' },
    meta: { requiresAuth: true }
  },

  // Catch all route
  {
    path: '*',
    redirect: (to) => {
      const token = localStorage.getItem('token');
      const isAuthenticated = store.getters['auth/isAuthenticated'];
      const userType = store.getters['auth/userType'];
      
      // Helper function to get dashboard route for user type
      const getDashboardRoute = (type) => {
        const routeMap = {
          'super_admin': '/super-admin/dashboard',
          'admin': '/admin/dashboard',
          'judge': '/judge/dashboard',
          'performer': '/performer/dashboard',
          'instructor': '/instructor/dashboard',
          'production_manager': '/production-manager/dashboard'
        };
        // For dynamic/custom roles, use pattern /${type}/dashboard
        // This will match the dynamic route pattern we defined above
        return routeMap[type] || `/${type}/dashboard`;
      };
      
      // Prevent infinite redirects - if we're already trying to go to a dashboard route
      if (to.path && to.path.includes('/dashboard')) {
        // List of valid specific dashboard routes
        const validSpecificRoutes = [
          '/super-admin/dashboard',
          '/admin/dashboard',
          '/judge/dashboard',
          '/performer/dashboard',
          '/instructor/dashboard',
          '/production-manager/dashboard'
        ];
        // If it's a valid specific route, don't redirect (shouldn't reach catch-all, but safety check)
        if (validSpecificRoutes.includes(to.path)) {
          return false; // Don't redirect
        }
        // For dynamic routes or invalid routes, use generic dashboard to break any loop
        return '/dashboard';
      }
      
      // If we have a token but auth isn't initialized yet, wait
      if (token && !isAuthenticated) {
        return '/login'; // Will be redirected after auth init
      }
      
      if (isAuthenticated && userType) {
        const dashboardRoute = getDashboardRoute(userType);
        // Prevent redirecting to the same path we're already on
        if (to.path === dashboardRoute) {
          return '/dashboard'; // Fallback to generic dashboard to break the loop
        }
        return dashboardRoute;
      }
      return '/login';
    }
  }
];

const CHUNK_RELOAD_KEY = 'cuetrics_chunk_reload_at';

function isChunkLoadError(error) {
  if (!error) return false;
  const message = error.message || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk [\d]+ failed/i.test(message)
  );
}

function handleChunkLoadError() {
  const now = Date.now();
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  } catch (e) {
    // sessionStorage unavailable
  }

  // Auto-reload once so users pick up the latest deploy after a stale bundle mismatch.
  if (!lastReloadAt || now - lastReloadAt > 30000) {
    try {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch (e) {
      // ignore
    }
    window.location.reload();
    return true;
  }

  store.dispatch(
    'showError',
    'A new version of the app is available. Please refresh the page to continue.'
  );
  return true;
}

function isIgnorableRouterError(error) {
  return (
    error.name === 'NavigationRedirectedError' ||
    error.name === 'NavigationDuplicated' ||
    error.message?.includes('Redirected') ||
    error.message?.includes('via a navigation guard') ||
    error.message?.includes('Avoided redundant navigation')
  );
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  }
});

// Navigation guards
router.beforeEach(async (to, from, next) => {
  // Wait for auth to be initialized if we have a token
  const token = localStorage.getItem('token');
  if (token && !store.getters['auth/isAuthenticated']) {
    let authResult;
    try {
      authResult = await store.dispatch('auth/initializeAuth');
    } catch (error) {
      authResult = {
        authenticated: false,
        error
      };
    }
    // Check if authentication failed
    if (!authResult || !authResult.authenticated) {
      console.error('Auth initialization failed in router guard:', authResult?.error);
      // Clear invalid token and redirect to login
      store.dispatch('auth/logout');
      if (to.path !== '/login') {
        next({ path: '/login', replace: true });
        return;
      }
    }
  }

  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const userType = store.getters['auth/userType'];

  // Helper function to get dashboard route for user type
  const getDashboardRoute = (type) => {
    const routeMap = {
      'super_admin': '/super-admin/dashboard',
      'admin': '/admin/dashboard',
      'judge': '/judge/dashboard',
      'performer': '/performer/dashboard'
    };
    // For dynamic roles (instructor, production_manager, custom roles), use pattern
    return routeMap[type] || `/${type}/dashboard`;
  };

  // Helper function to get component name from route path
  const getComponentForRoute = (path, userType) => {
    // Map route patterns to component names
    const routePatterns = {
      'calendar': 'Calendar',
      'auditions': 'Auditions',
      'sessions': 'Auditions', // Judge sessions are also Auditions
      'productions': 'Productions',
      'classes': 'Classes',
      'attendance': 'Attendance',
      'users': 'Users',
      'registrations': 'Users', // Registrations maps to Users component
      'meetings': 'Meetings',
      'results': 'Results',
      'schedule': 'Calendar' // Judge schedule is Calendar
    };

    // Extract the component name from path like /${userType}/component
    const pathParts = path.split('/').filter(p => p);
    if (pathParts.length >= 2 && pathParts[0] === userType) {
      const componentPath = pathParts[1];
      return routePatterns[componentPath] || null;
    }

    // Handle specific route mappings for backward compatibility
    const specificRouteMap = {
      '/judge/sessions': 'Auditions',
      '/judge/schedule': 'Calendar',
      '/instructor/classes': 'Classes',
      '/instructor/attendance': 'Attendance',
      '/instructor/calendar': 'Calendar',
      '/production-manager/productions': 'Productions',
      '/production-manager/auditions': 'Auditions',
      '/production-manager/registrations': 'Users',
      '/production-manager/calendar': 'Calendar'
    };

    return specificRouteMap[path] || null;
  };

  // Check if route requires authentication
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!isAuthenticated) {
      next({ path: '/login', replace: true });
      return;
    }

    // Check role-specific permissions
    if (to.meta.requiresSuperAdmin && userType !== 'super_admin') {
      next({ path: getDashboardRoute(userType), replace: true });
      return;
    }

    // Super admin must be in "view as company" mode to use company-admin routes
    if (
      userType === 'super_admin' &&
      to.path.startsWith('/admin') &&
      !to.path.startsWith('/admin/permissions') &&
      !store.getters['auth/isViewingAsCompany']
    ) {
      next({ path: '/super-admin/dashboard', replace: true });
      return;
    }

    if (to.meta.requiresAdmin && userType !== 'admin' && userType !== 'super_admin') {
      next({ path: getDashboardRoute(userType), replace: true });
      return;
    }

    if (to.meta.requiresJudge && userType !== 'judge' && !store.state.auth.user?.canJudge) {
      // Allow admin users to access judge routes for test auditions and summary/casting
      if (userType === 'admin' && (
        to.path === '/judge/scoring' ||
        to.path.includes('/judge/auditions/') && (to.path.includes('/summary') || to.path.includes('/casting')) ||
        to.path === '/judge/test-scoring' ||
        to.path === '/judge/template-preview'
      )) {
        // Admin users can access these routes
        next();
        return;
      }
      next({ path: getDashboardRoute(userType), replace: true });
      return;
    }

    if (to.meta.requiresPerformer && userType !== 'performer') {
      next({ path: getDashboardRoute(userType), replace: true });
      return;
    }

    // Check permissions for dynamic user types (any non-universal role)
    // Universal roles: super_admin, admin, performer (they don't use permissions for route access)
    const universalRoles = ['super_admin', 'admin', 'performer'];

    // Assigned scorers (canJudge) are authorized for the judge/scoring routes by
    // their assignment, not by role permissions. Skip the component-permission gate
    // for them on requiresJudge routes, otherwise a production_manager/instructor
    // scorer gets redirected off /judge/* because canView('Auditions') is false. (CUET-89)
    const isAssignedScorerOnJudgeRoute = to.meta.requiresJudge && store.state.auth.user?.canJudge;

    if (!universalRoles.includes(userType) && !isAssignedScorerOnJudgeRoute) {
      // This includes judge, instructor, production_manager, and any custom roles
      // Ensure permissions are loaded
      if (!store.state.permissions.userPermissions) {
        try {
          await store.dispatch('permissions/fetchPermissions');
        } catch (error) {
          console.error('Failed to fetch permissions:', error);
        }
      }

      // Get component name from route path dynamically
      const componentName = getComponentForRoute(to.path, userType);
      if (componentName) {
        const canView = store.getters['permissions/canView'](componentName);
        if (!canView) {
          return next({ path: getDashboardRoute(userType) });
        }
      }
    }

    // Admin users are universal - they have full access, no permission checks needed
    // (Admin users cannot have custom roles, so they always have all permissions)
    // Skip permission checks for admin users
  }

  // Check if route requires guest (not authenticated)
  // Exception: setup-account routes should be accessible even when authenticated
  // (users might need to set up an account they were invited to)
  if (to.matched.some(record => record.meta.requiresGuest)) {
    if (isAuthenticated && !to.path.startsWith('/setup-account')) {
      // Redirect to role-specific dashboard instead of generic dashboard
      next({ path: getDashboardRoute(userType), replace: true });
      return;
    }
  }

  next();
});

// Handle navigation errors gracefully
router.onError((error) => {
  if (isIgnorableRouterError(error)) return;
  if (isChunkLoadError(error)) {
    handleChunkLoadError();
    return;
  }

  console.error('Router navigation error:', error);
  store.dispatch('showError', 'Navigation failed. Please try again.');
});

// Wrap router.push to handle navigation errors globally
const originalPush = router.push;
router.push = function push(location, onComplete, onAbort) {
  // If no callbacks provided, add default error handler
  if (!onComplete && !onAbort) {
    return originalPush.call(this, location).catch(err => {
      // Suppress expected navigation errors
      if (
        err.name === 'NavigationDuplicated' ||
        err.name === 'NavigationRedirectedError' ||
        err.message?.includes('Avoided redundant navigation') ||
        err.message?.includes('Redirected') ||
        err.message?.includes('via a navigation guard')
      ) {
        // Silently ignore these expected errors
        return Promise.resolve();
      }
      // Re-throw unexpected errors
      return Promise.reject(err);
    });
  }
  // If callbacks are provided, use them but also catch errors
  const pushResult = originalPush.call(this, location, onComplete, onAbort);
  if (pushResult && typeof pushResult.catch === 'function') {
    return pushResult.catch(err => {
      // Suppress expected navigation errors
      if (
        err.name === 'NavigationDuplicated' ||
        err.name === 'NavigationRedirectedError' ||
        err.message?.includes('Avoided redundant navigation') ||
        err.message?.includes('Redirected')
      ) {
        // Silently ignore these expected errors
        return Promise.resolve();
      }
      // Call onAbort if provided for unexpected errors
      if (onAbort && typeof onAbort === 'function') {
        onAbort(err);
      }
      // Re-throw unexpected errors
      return Promise.reject(err);
    });
  }
  return pushResult;
};

// Also wrap router.replace for consistency
const originalReplace = router.replace;
router.replace = function replace(location, onComplete, onAbort) {
  // If no callbacks provided, add default error handler
  if (!onComplete && !onAbort) {
    return originalReplace.call(this, location).catch(err => {
      // Suppress expected navigation errors
      if (
        err.name === 'NavigationDuplicated' ||
        err.name === 'NavigationRedirectedError' ||
        err.message?.includes('Avoided redundant navigation') ||
        err.message?.includes('Redirected') ||
        err.message?.includes('via a navigation guard')
      ) {
        // Silently ignore these expected errors
        return Promise.resolve();
      }
      // Re-throw unexpected errors
      return Promise.reject(err);
    });
  }
  // If callbacks are provided, use them but also catch errors
  const replaceResult = originalReplace.call(this, location, onComplete, onAbort);
  if (replaceResult && typeof replaceResult.catch === 'function') {
    return replaceResult.catch(err => {
      // Suppress expected navigation errors
      if (
        err.name === 'NavigationDuplicated' ||
        err.name === 'NavigationRedirectedError' ||
        err.message?.includes('Avoided redundant navigation') ||
        err.message?.includes('Redirected')
      ) {
        // Silently ignore these expected errors
        return Promise.resolve();
      }
      // Call onAbort if provided for unexpected errors
      if (onAbort && typeof onAbort === 'function') {
        onAbort(err);
      }
      // Re-throw unexpected errors
      return Promise.reject(err);
    });
  }
  return replaceResult;
};

export default router; 
